
import { computed, defineComponent, ref, watch } from "vue";
import { useStore } from "vuex";
import store from "@/store";
import MailsListItem from "@/components/mails/MailsListItem.vue";
import AppIcon from "@/components/common/AppIcon.vue";
import AppButton from "@/components/common/AppButton.vue";
import AppSection from "@/components/common/AppSection.vue";
import CardKpi from "@/components/card/CardKpi.vue";
import CardGroup from "@/components/card/CardGroup.vue";
import DropdownMenu from "@/components/common/dropdown/DropdownMenu.vue";
import DropdownMenuItem from "@/components/common/dropdown/DropdownMenuItem.vue";
import BounceMetrics from "@/components/mails/BounceMetrics.vue";
import useKPIs from "@/composables/useKPIs";
import useBounceAnalytics from "@/composables/useBounceAnalytics";
import axios from "axios";

export default defineComponent({
    name: "MailsListPage",
    components: {
        CardGroup,
        CardKpi,
        AppIcon,
        AppButton,
        AppSection,
        DropdownMenu,
        DropdownMenuItem,
        MailsListItem,
        BounceMetrics
    },
    async beforeRouteEnter(to, from, next) {
        // If we already have emails, we reload in the background
        if (store.state.mails.total > 0) {
            store.dispatch("mails/fetchList").finally();
            next();
        } else {
            store.commit("app/SET_LOADING", true);
            await store.dispatch("mails/fetchList").then(() => {
                store.commit("app/SET_LOADING", false);
            });
            next();
        }
    },
    setup() {
        const store = useStore();
        const { kpiDays, kpis, kpisLoading } = useKPIs();
        const {
            bounceData,
            loading: bounceLoading,
            fetchBounceAnalytics
        } = useBounceAnalytics();

        const currency = computed(() => store.state.store.currency);
        const exportLoading = ref(false);
        const showBounceMetrics = ref(false);

        // Watch for changes in kpiDays to sync bounce analytics with KPI period
        watch(
            kpiDays,
            newPeriod => {
                fetchBounceAnalytics(newPeriod);
            },
            { immediate: true }
        );

        const exportCampaignReport = async () => {
            exportLoading.value = true;

            try {
                const response = await axios({
                    url: "api/emails/export-campaign-report",
                    method: "GET",
                    responseType: "blob"
                });

                const url = window.URL.createObjectURL(
                    new Blob([response.data])
                );
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute(
                    "download",
                    `email_campaign_report_${
                        new Date().toISOString().split("T")[0]
                    }.csv`
                );
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error("Export failed:", error);
                // TODO: Add proper error handling/notification
            } finally {
                exportLoading.value = false;
            }
        };

        return {
            kpis,
            kpiDays,
            kpisLoading,
            bounceData,
            bounceLoading,
            exportLoading,
            showBounceMetrics,
            activeMails: computed(() => store.getters["mails/activeList"]),
            scheduledMails: computed(
                () => store.getters["mails/scheduledList"]
            ),
            draftMails: computed(() => store.getters["mails/draftsList"]),
            pastMails: computed(() => store.getters["mails/pastList"]),
            currency,
            exportCampaignReport
        };
    }
});
